import { Check } from "lucide-react";
import { Tip } from "./ui/tooltip";

export interface WorkflowStep {
  id: string;
  label: string;
  description?: string;
}

type StepStatus = "completed" | "active" | "pending";

interface WorkflowStepperProps {
  steps: WorkflowStep[];
  currentStep: number;
  orientation?: "horizontal" | "vertical";
  /**
   * When provided, completed (already-visited) steps become clickable so the
   * user can jump back to a previous step. Called with the target step index.
   * Future/pending steps stay non-clickable so validation can't be skipped.
   */
  onStepClick?: (index: number) => void;
}

export function WorkflowStepper({
  steps,
  currentStep,
  orientation = "horizontal",
  onStepClick,
}: WorkflowStepperProps) {
  const getStatus = (i: number): StepStatus => {
    if (i < currentStep) return "completed";
    if (i === currentStep) return "active";
    return "pending";
  };

  if (orientation === "vertical") {
    return (
      <div className="space-y-0">
        {steps.map((step, i) => {
          const status = getStatus(i);
          return (
            <div key={step.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div
                  className={`size-7 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 z-10
                    ${status === "completed" ? "bg-emerald-600 dark:bg-emerald-500 text-white" : ""}
                    ${status === "active" ? "bg-indigo-600 dark:bg-indigo-600 text-white ring-2 ring-indigo-200 dark:ring-indigo-500/30" : ""}
                    ${status === "pending" ? "border-2 border-indigo-200 dark:border-indigo-500/20 text-indigo-400 dark:text-indigo-400/50 bg-background" : ""}
                  `}
                >
                  {status === "completed" ? (
                    <Check className="size-3.5" />
                  ) : (
                    i + 1
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={`w-px flex-1 my-1 min-h-[20px] ${i < currentStep ? "bg-emerald-500 dark:bg-emerald-500" : "bg-[#e2e8f0] dark:bg-white/[0.1]"}`}
                  />
                )}
              </div>
              <div
                className={`pb-4 min-w-0 ${i === steps.length - 1 ? "" : "pb-5"}`}
              >
                <p
                  className={`text-sm font-medium ${status === "completed" ? "text-emerald-600 dark:text-emerald-400" : status === "active" ? "text-indigo-700 dark:text-indigo-300" : "d"}`}
                >
                  {step.label}
                </p>
                {step.description && (
                  <p className="text-xs d mt-0.5">{step.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center">
      {steps.map((step, i) => {
        const status = getStatus(i);
        const clickable = !!onStepClick && status === "completed";
        const StepTag = clickable ? "button" : "div";
        const tipLabel = clickable
          ? `Go back to ${step.label}`
          : (step.description ?? step.label);
        return (
          <div
            key={step.id}
            className="flex items-center flex-1 last:flex-none"
          >
            <Tip label={tipLabel} side="top">
            <StepTag
              {...(clickable
                ? {
                    type: "button" as const,
                    onClick: () => onStepClick!(i),
                  }
                : {})}
              className={`flex flex-col items-center gap-1 ${clickable ? "cursor-pointer hover:opacity-80 transition-opacity focus:outline-none" : ""}`}
            >
              <div
                className={`size-7 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0
                  ${status === "completed" ? "bg-emerald-600 dark:bg-emerald-500 text-white" : ""}
                  ${status === "active" ? "bg-indigo-600 dark:bg-indigo-600 text-white ring-2 ring-indigo-200 dark:ring-indigo-500/30" : ""}
                  ${status === "pending" ? "border-2 border-indigo-200 dark:border-indigo-500/20 text-indigo-400 dark:text-indigo-400/50 bg-background" : ""}
                `}
              >
                {status === "completed" ? (
                  <Check className="size-3.5" />
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`text-xs whitespace-nowrap ${
                  status === "completed"
                    ? "text-emerald-600 dark:text-emerald-400 font-medium"
                    : status === "active"
                      ? "text-indigo-700 dark:text-indigo-300 font-medium"
                      : "d"
                }`}
              >
                {step.label}
              </span>
            </StepTag>
            </Tip>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-px mx-2 mb-4 ${i < currentStep ? "bg-emerald-500 dark:bg-emerald-500" : "bg-[#e2e8f0] dark:bg-white/[0.1]"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
